import { Injectable, inject } from '@angular/core';
import {
  Auth,
  EmailAuthProvider,
  User,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updatePassword,
} from '@angular/fire/auth';
import { Store } from '@ngrx/store';
import { Observable, catchError, from, of, switchMap } from 'rxjs';
import { AppExceptionModel } from '../models/app-exception.model';
import { UserModel } from '../models/user.model';
import * as userActions from '../state/actions/user.actions';
import { AppState } from '../state/appstate';
import { AnalyticsService } from './analytics.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth: Auth = inject(Auth);
  private analyticsService: AnalyticsService = inject(AnalyticsService);

  logout(): Promise<void> {
    return this.auth.signOut();
  }

  constructor(private store: Store<AppState>) {
    this.auth
      .setPersistence(browserLocalPersistence)
      .then(() => {
        // console.log('AuthService :: Auth persistence set to local');
      })
      .catch((error) => {
        // console.error('Error setting persistence:', error);
      });
    onAuthStateChanged(this.auth, async (user) => {
      // console.log(
      //   `AuthService :: onAuthStateChanged: Uid: ${user?.uid}, Email: ${user?.email}, Email Verified: ${user?.emailVerified}`
      // );
      if (user) {
        if (user.emailVerified) {
          this.analyticsService.setUserId(user.uid);
          this.store.dispatch(
            userActions.authenticated({
              user: {
                uid: user.uid,
                email: user.email,
                name: user.displayName,
                photoURL: user.photoURL,
              } as UserModel,
            }),
          );
        } else {
          this.store.dispatch(userActions.logout());
        }
      } else {
        this.analyticsService.setUserId(null);
        this.store.dispatch(userActions.notAuthenticated());
      }
    });
  }

  onAuthChanged() {
    return onAuthStateChanged(this.auth, (user) => user);
  }

  isAuthenticated(): boolean {
    return (
      this.auth?.currentUser !== null && this.auth?.currentUser.emailVerified
    );
  }

  // Check if user is authenticated
  async getToken(): Promise<string | null> {
    const token = await this.auth.currentUser?.getIdToken();
    return token ?? null;
  }

  // get uid of current user
  getUid(): string | undefined {
    return this.auth.currentUser?.uid;
  }

  // get display name of current user
  getUserName(): string | undefined {
    return this.auth.currentUser?.displayName || undefined;
  }

  // get photo URL of current user
  getUserPhotoURL(): string | undefined {
    return this.auth.currentUser?.photoURL || undefined;
  }

  async signUp(email: string, password: string): Promise<void> {
    return createUserWithEmailAndPassword(this.auth, email, password)
      .then((userCredential) => {
        const user = userCredential.user;
        return;
      })
      .catch((error) => {
        let errorCode = 'Authentication Error';
        switch (error.code) {
          case 'auth/email-already-in-use':
            errorCode = 'Email-already-in-use';
            break;
          case 'auth/weak-password':
            errorCode = 'Weak-password';
            break;
          case 'auth/invalid-email':
            errorCode = 'Invalid-email';
            break;
          case 'auth/operation-not-allowed':
            errorCode = 'Operation-not-allowed';
            break;
          default:
            errorCode = error.code;
            break;
        }
        const errorMessage = error.message;
        throw { code: errorCode, message: errorMessage } as AppExceptionModel;
      });
  }

  loginWithPassword(email: string, password: string): Observable<User | null> {
    const auth = getAuth();
    return from(signInWithEmailAndPassword(auth, email, password)).pipe(
      switchMap((userCredential) => {
        const user = userCredential.user;
        this.analyticsService.logLogin('email');
        return of(user);
      }),
      catchError((error) => {
        const errorCode = error.code;
        const errorMessage = error.message.replace('Firebase: ', '');
        const exception: AppExceptionModel = {
          code: errorCode,
          message: errorMessage,
        };
        throw exception;
      }),
    );
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user || !user.email) {
      throw {
        code: 'auth/user-not-found',
        message: 'No authenticated user found.',
      } as AppExceptionModel;
    }

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
    } catch (error: any) {
      let errorMessage = 'Failed to update password.';
      if (error?.code) {
        switch (error.code) {
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
            errorMessage = 'Current password is incorrect.';
            break;
          case 'auth/weak-password':
            errorMessage = 'New password should be at least 6 characters.';
            break;
          case 'auth/requires-recent-login':
            errorMessage = 'Please log out and log in again before updating your password.';
            break;
          default:
            errorMessage = error.message?.replace('Firebase: ', '') || error.code;
            break;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      throw {
        code: error?.code || 'auth/error',
        message: errorMessage,
      } as AppExceptionModel;
    }
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error: any) {
      let errorMessage = 'Failed to send password reset email.';
      if (error?.code) {
        switch (error.code) {
          case 'auth/invalid-email':
            errorMessage = 'Please enter a valid email address.';
            break;
          case 'auth/user-not-found':
            errorMessage = 'No account found with this email address.';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Too many requests. Please try again later.';
            break;
          default:
            errorMessage = error.message?.replace('Firebase: ', '') || error.code;
            break;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      throw {
        code: error?.code || 'auth/error',
        message: errorMessage,
      } as AppExceptionModel;
    }
  }
}

